import { z } from "zod";

import { cableSizingStatusSchema, conductorMaterialSchema, insulationMaterialSchema } from "./cableContract";
import { faultTypeSchema, shortCircuitCaseSchema } from "./faultContract";

// The controlled vocabularies of the earth-fault loop impedance and automatic disconnection
// check (EOS-05d, Master Prompt A33), copied verbatim from the backend so a value the API can
// send is never rejected here. Sources at commit 8d2a001:
// backend/app/domain/electrical/earth_fault_loop/models.py (SystemKind, SupplyKind,
// CircuitKind, DeviceKind, McbType, OperatingCurrentBasis, CableFiguresSource,
// TemperatureBasis) and .../results.py (EarthFaultLoopStatus, EarthFaultLoopWarningCode and
// the four severity sets, ExternalImpedanceOrigin, VoltageFactorOrigin, LimitBasis,
// ResistanceBasis, DisconnectionTimeOrigin, OperatingCurrentOrigin,
// MaximumLoopImpedanceOrigin). earthFaultLoopContract.test.ts pins each list.
//
// The conductor material, the insulation and the cable run status are the cable module's
// enums, the case and fault type of the linked fault run the fault module's: reused from
// those contracts rather than written out a second time.

export const earthFaultLoopStatusSchema = z.enum([
  "VALID",
  "WARNING",
  "REVIEW_REQUIRED",
  "NO_SOLUTION",
]);

export const earthFaultLoopWarningCodeSchema = z.enum([
  "DISCONNECTION_NOT_ASSURED",
  "SYSTEM_KIND_REFUSED",
  "EXTERNAL_IMPEDANCE_NOT_ESTABLISHED",
  "EXTERNAL_IMPEDANCE_SOURCE_NOT_DECLARED",
  "FAULT_RUN_NOT_MINIMUM_EARTH_FAULT",
  "FAULT_RUN_BUS_MISMATCH",
  "VOLTAGE_FACTOR_SOURCE_NOT_DECLARED",
  "NOMINAL_VOLTAGE_SOURCE_NOT_DECLARED",
  "SYSTEM_KIND_NOT_ASSESSED",
  "CONDUCTOR_TEMPERATURE_NOT_DECLARED",
  "CONDUCTOR_TEMPERATURE_SOURCE_NOT_DECLARED",
  "CABLE_FIGURES_SOURCE_NOT_DECLARED",
  "CABLE_RUN_NOT_VALID",
  "PROTECTIVE_CONDUCTOR_NOT_ESTABLISHED",
  "DISCONNECTION_TIME_NOT_ESTABLISHED",
  "DISCONNECTION_TIME_SOURCE_NOT_DECLARED",
  "DEVICE_OPERATING_CURRENT_NOT_ESTABLISHED",
  "DEVICE_CHARACTERISTIC_SOURCE_NOT_DECLARED",
  "LIMIT_NOT_ESTABLISHED",
  "DECLARED_ZS_MAX_DIFFERS_FROM_IA",
  "MARGIN_BELOW_DECLARED_MINIMUM",
  "TEMPERATURE_ABOVE_INSULATION_MAXIMUM",
  "REGISTER_TABLE_MEASURED_VALUE_BASIS",
  "REGISTER_TABLE_NOT_APPLICABLE",
  "DECLARED_VALUE_GOVERNS_OVER_REGISTER_TABLE",
  "REGISTER_TIME_BASIS",
  "REGISTER_TIME_NOT_APPLICABLE",
  "DECLARED_TIME_GOVERNS_OVER_REGISTER_TABLE",
  "NOMINAL_VOLTAGE_NEAR_BAND_BOUNDARY",
  "CABLE_RUN_NOT_LINKED",
  "RESISTANCE_DECLARED_GOVERNS",
  "CABLE_REACTANCE_NOT_INCLUDED",
  "ARITHMETIC_SUM_OF_MAGNITUDES",
  "VOLTAGE_FACTOR_NOT_APPLIED",
  "DEVICE_CHARACTERISTIC_DECLARED",
  "PROTECTIVE_CONDUCTOR_THERMAL_NOT_CHECKED",
  "TOUCH_VOLTAGE_NOT_EVALUATED",
  "ADDITIONAL_PROTECTION_NOT_ASSESSED",
]);

export const systemKindSchema = z.enum(["TN_S", "TN_C_S", "TN_C", "TT", "IT"]);
export const supplyKindSchema = z.enum(["THREE_PHASE_FOUR_WIRE", "SINGLE_PHASE"]);
export const circuitKindSchema = z.enum(["FINAL", "DISTRIBUTION"]);
export const deviceKindSchema = z.enum(["MCB", "MCCB", "ACB", "FUSE", "RCD", "OTHER"]);
export const mcbTypeSchema = z.enum(["B", "C", "D", "NOT_APPLICABLE"]);
export const operatingCurrentBasisSchema = z.enum([
  "TIME_CURRENT_DATA",
  "STANDARD_TABLE",
  "PROJECT_DESIGN_BASIS",
  "RCD_RATED_RESIDUAL_CURRENT",
]);
export const cableFiguresSourceSchema = z.enum(["LINKED_CABLE_RUN", "DECLARED"]);
export const temperatureBasisSchema = z.enum(["LINKED_CABLE_INSULATION", "DECLARED"]);
export const externalImpedanceOriginSchema = z.enum([
  "LINKED_FAULT_RUN",
  "DECLARED",
  "NOT_ESTABLISHED",
]);
export const voltageFactorOriginSchema = z.enum(["DECLARED", "NOT_APPLIED"]);
export const limitBasisSchema = z.enum(["REGISTER_VERIFIED", "DECLARED", "NOT_ESTABLISHED"]);
export const resistanceBasisSchema = z.enum(["RHO_OVER_S", "DECLARED_R20"]);
export const disconnectionTimeOriginSchema = z.enum([
  "DECLARED",
  "REGISTER_VERIFIED",
  "NOT_ESTABLISHED",
]);
export const operatingCurrentOriginSchema = z.enum([
  "DECLARED",
  "IMPLIED_FROM_MAXIMUM_LOOP_IMPEDANCE",
  "NOT_ESTABLISHED",
]);
export const maximumLoopImpedanceOriginSchema = z.enum([
  "FROM_OPERATING_CURRENT",
  "DECLARED",
  "REGISTER_VERIFIED",
  "NOT_ESTABLISHED",
]);

export {
  cableSizingStatusSchema,
  conductorMaterialSchema,
  faultTypeSchema,
  insulationMaterialSchema,
  shortCircuitCaseSchema,
};

/** The condition evaluated and not met, or the system kind refused (NO_SOLUTION). */
export const NO_SOLUTION_WARNING_CODES = ["DISCONNECTION_NOT_ASSURED", "SYSTEM_KIND_REFUSED"] as const;

/** A source or a basis the engineer still owes, a link that is not the right evidence, or a declared limit (GAP-027). */
export const REVIEW_REQUIRED_WARNING_CODES = [
  "EXTERNAL_IMPEDANCE_NOT_ESTABLISHED",
  "EXTERNAL_IMPEDANCE_SOURCE_NOT_DECLARED",
  "FAULT_RUN_NOT_MINIMUM_EARTH_FAULT",
  "FAULT_RUN_BUS_MISMATCH",
  "VOLTAGE_FACTOR_SOURCE_NOT_DECLARED",
  "NOMINAL_VOLTAGE_SOURCE_NOT_DECLARED",
  "SYSTEM_KIND_NOT_ASSESSED",
  "CONDUCTOR_TEMPERATURE_NOT_DECLARED",
  "CONDUCTOR_TEMPERATURE_SOURCE_NOT_DECLARED",
  "CABLE_FIGURES_SOURCE_NOT_DECLARED",
  "CABLE_RUN_NOT_VALID",
  "PROTECTIVE_CONDUCTOR_NOT_ESTABLISHED",
  "DISCONNECTION_TIME_NOT_ESTABLISHED",
  "DISCONNECTION_TIME_SOURCE_NOT_DECLARED",
  "DEVICE_OPERATING_CURRENT_NOT_ESTABLISHED",
  "DEVICE_CHARACTERISTIC_SOURCE_NOT_DECLARED",
  "LIMIT_NOT_ESTABLISHED",
] as const;

/** Met, but the engineer must look. */
export const WARNING_WARNING_CODES = [
  "DECLARED_ZS_MAX_DIFFERS_FROM_IA",
  "MARGIN_BELOW_DECLARED_MINIMUM",
  "TEMPERATURE_ABOVE_INSULATION_MAXIMUM",
] as const;

/** What the sheet states; never moves the status. */
export const INFORMATION_WARNING_CODES = [
  "REGISTER_TABLE_MEASURED_VALUE_BASIS",
  "REGISTER_TABLE_NOT_APPLICABLE",
  "DECLARED_VALUE_GOVERNS_OVER_REGISTER_TABLE",
  "REGISTER_TIME_BASIS",
  "REGISTER_TIME_NOT_APPLICABLE",
  "DECLARED_TIME_GOVERNS_OVER_REGISTER_TABLE",
  "NOMINAL_VOLTAGE_NEAR_BAND_BOUNDARY",
  "CABLE_RUN_NOT_LINKED",
  "RESISTANCE_DECLARED_GOVERNS",
  "CABLE_REACTANCE_NOT_INCLUDED",
  "ARITHMETIC_SUM_OF_MAGNITUDES",
  "VOLTAGE_FACTOR_NOT_APPLIED",
  "DEVICE_CHARACTERISTIC_DECLARED",
  "PROTECTIVE_CONDUCTOR_THERMAL_NOT_CHECKED",
  "TOUCH_VOLTAGE_NOT_EVALUATED",
  "ADDITIONAL_PROTECTION_NOT_ASSESSED",
] as const;

/** The three items every row states it did not check (D14, D12 - the F-1 lesson of IR-005). */
export const ALWAYS_ROW_INFORMATION_CODES = [
  "PROTECTIVE_CONDUCTOR_THERMAL_NOT_CHECKED",
  "TOUCH_VOLTAGE_NOT_EVALUATED",
  "ADDITIONAL_PROTECTION_NOT_ASSESSED",
] as const;

export type EarthFaultLoopStatus = z.infer<typeof earthFaultLoopStatusSchema>;
export type EarthFaultLoopWarningCode = z.infer<typeof earthFaultLoopWarningCodeSchema>;
export type SystemKind = z.infer<typeof systemKindSchema>;
export type SupplyKind = z.infer<typeof supplyKindSchema>;
export type CircuitKind = z.infer<typeof circuitKindSchema>;
export type DeviceKind = z.infer<typeof deviceKindSchema>;
export type McbType = z.infer<typeof mcbTypeSchema>;
export type OperatingCurrentBasis = z.infer<typeof operatingCurrentBasisSchema>;
export type CableFiguresSource = z.infer<typeof cableFiguresSourceSchema>;
export type TemperatureBasis = z.infer<typeof temperatureBasisSchema>;
export type ExternalImpedanceOrigin = z.infer<typeof externalImpedanceOriginSchema>;
export type VoltageFactorOrigin = z.infer<typeof voltageFactorOriginSchema>;
export type LimitBasis = z.infer<typeof limitBasisSchema>;
export type ResistanceBasis = z.infer<typeof resistanceBasisSchema>;
export type DisconnectionTimeOrigin = z.infer<typeof disconnectionTimeOriginSchema>;
export type OperatingCurrentOrigin = z.infer<typeof operatingCurrentOriginSchema>;
export type MaximumLoopImpedanceOrigin = z.infer<typeof maximumLoopImpedanceOriginSchema>;
